import { useState, useEffect, useCallback, type FormEvent } from "react";
import { useLocation } from "wouter";
import { useAuth, type TwoFaChallenge } from "@/contexts/auth";
import { GoogleGsiButton } from "@/components/GoogleGsiButton";
import { Link } from "wouter";
import { ForgotPasswordModal } from "@/components/ForgotPasswordModal";
import { ShieldCheck, Mail, Smartphone, RefreshCw, KeyRound, Eye, EyeOff, Loader2, CheckCircle2 } from "lucide-react";
import { useLanguage } from "@/contexts/language";
import { useTurnstile } from "@/hooks/useTurnstile";
import bgDayUrl from "../assets/bg-day.png";
import bgNightUrl from "../assets/bg-night.png";
import logoUrl from "../assets/logo.png";
const supportEmail = ["support", "flightdecktrainer.ca"].join("@");
const handleSupport = () => {
  const isLoggedIn = Boolean(localStorage.getItem("token"));
  if (isLoggedIn) { window.location.href = "/support"; }
  else { window.location.href = "mailto:" + supportEmail; }
};



/**
 * Returns true if the visitor's local time is within civil daylight hours.
 * Civil twilight boundaries are approximated by month (North American latitudes ~45-55°N).
 * All times are compared against the browser's local clock — no server timezone.
 *
 *  Month  | Morning CT (start of day) | Evening CT (end of day)
 *  -------|---------------------------|------------------------
 *  Jan    | 07:30                     | 17:00
 *  Feb    | 07:00                     | 17:45
 *  Mar    | 06:15                     | 18:30
 *  Apr    | 06:00                     | 20:00  (DST begins)
 *  May    | 05:30                     | 20:45
 *  Jun    | 05:00                     | 21:15
 *  Jul    | 05:15                     | 21:00
 *  Aug    | 05:45                     | 20:15
 *  Sep    | 06:30                     | 19:15
 *  Oct    | 07:00                     | 18:00  (DST ends)
 *  Nov    | 07:30                     | 17:00
 *  Dec    | 07:45                     | 16:45
 */
function isDaytime(): boolean {
  const now = new Date();
  const month = now.getMonth(); // 0 = Jan … 11 = Dec
  const localTime = now.getHours() + now.getMinutes() / 60;

  // [morningCivilTwilight, eveningCivilTwilight] in decimal local hours
  const civilTwilight: [number, number][] = [
    [7.5,  17.0],  // Jan
    [7.0,  17.75], // Feb
    [6.25, 18.5],  // Mar
    [6.0,  20.0],  // Apr
    [5.5,  20.75], // May
    [5.0,  21.25], // Jun
    [5.25, 21.0],  // Jul
    [5.75, 20.25], // Aug
    [6.5,  19.25], // Sep
    [7.0,  18.0],  // Oct
    [7.5,  17.0],  // Nov
    [7.75, 16.75], // Dec
  ];

  const [dawn, dusk] = civilTwilight[month];
  return localTime >= dawn && localTime < dusk;
}

const inputCls =
  "w-full px-4 py-2.5 rounded-lg border border-white/20 bg-white/10 text-white " +
  "placeholder:text-white/40 focus:outline-none focus:ring-2 focus:ring-white/30 " +
  "focus:border-white/50 transition-all text-sm font-mono backdrop-blur-sm";

const labelCls = "block text-xs font-semibold text-white/60 uppercase tracking-wider mb-1.5";

const googleClientId = (import.meta.env.VITE_GOOGLE_CLIENT_ID as string | undefined)?.trim() ?? "";

function appHomePath(): string {
  const pathPrefix = (import.meta.env.BASE_URL || "/").replace(/\/$/, "");
  return pathPrefix ? `${pathPrefix}/` : "/";
}

export function LoginGate({ children }: { children: React.ReactNode }) {
  const [, setLocation] = useLocation();
  const {
    isAuthenticated,
    user,
    sessionHydrateError,
    hydrateSession,
    clearClientSession,
    login,
    loginWithGoogleCredential,
    completeLogin,
    resendOtp,
  } = useAuth();
  const { language, setLanguage, t } = useLanguage();
  const isFr = language === "fr";
  const lg = t.login;

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [accountLocked, setAccountLocked] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showForgot, setShowForgot] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [challenge, setChallenge] = useState<TwoFaChallenge | null>(null);
  const [twoFaCode, setTwoFaCode] = useState("");
  const [resendLoading, setResendLoading] = useState(false);
  const [resendMsg, setResendMsg] = useState("");
  const [useRecovery, setUseRecovery] = useState(false);

  const [captchaRequired, setCaptchaRequired] = useState(false);
  const [retryAfter, setRetryAfter] = useState(0);

  // Single Turnstile instance: visible widget, only mounted once captchaRequired=true
  const captchaTurnstile = useTurnstile({ mode: "visible", active: captchaRequired });

  // Countdown timer for temp lockout
  useEffect(() => {
    if (retryAfter <= 0) return;
    const timer = setTimeout(() => setRetryAfter(v => Math.max(0, v - 1)), 1_000);
    return () => clearTimeout(timer);
  }, [retryAfter]);

  const day = isDaytime();
  const bgImage = day ? bgDayUrl : bgNightUrl;

  // Token present but profile not loaded yet (return visit or rare login edge case).
  if (isAuthenticated && !user) {
    return (
      <div
        className="min-h-screen flex flex-col items-center justify-center gap-4 px-6 text-center"
        style={{ backgroundImage: `url(${bgImage})`, backgroundSize: "cover", backgroundPosition: "center" }}
      >
        <div className="absolute inset-0 bg-black/55" />
        <div className="relative z-10 flex flex-col items-center gap-3 max-w-sm">
          <Loader2 className="h-6 w-6 animate-spin text-white/70" aria-hidden />
          <p className="text-white/80 font-mono text-sm">{lg.finalizingSignIn}</p>
          {sessionHydrateError ? (
            <>
              <p className="text-amber-200/90 font-mono text-xs leading-relaxed">{sessionHydrateError}</p>
              <div className="flex flex-wrap items-center justify-center gap-2 pt-1">
                <button
                  type="button"
                  onClick={() => void hydrateSession()}
                  className="px-4 py-2 rounded-md bg-white/15 text-white text-xs font-mono font-semibold hover:bg-white/25 transition-colors"
                >
                  {lg.finalizingRetry}
                </button>
                <button
                  type="button"
                  onClick={() => clearClientSession()}
                  className="px-4 py-2 rounded-md border border-white/25 text-white/80 text-xs font-mono hover:bg-white/10 transition-colors"
                >
                  {lg.finalizingBackToLogin}
                </button>
              </div>
            </>
          ) : null}
        </div>
      </div>
    );
  }
  if (isAuthenticated) return <>{children}</>;

  const handleGoogleLogin = useCallback(async (credential: string) => {
    setError("");
    setAccountLocked(false);
    setRetryAfter(0);
    setLoading(true);
    try {
      clearClientSession();
      const result = await loginWithGoogleCredential(credential, "login", rememberMe);
      if (result && "requires2fa" in result && result.requires2fa) {
        setChallenge(result);
        return;
      }
      setLocation(appHomePath());
    } catch (err) {
      const e = err as Error & { accountLocked?: boolean; retryAfter?: number };
      if (e.accountLocked) setAccountLocked(true);
      if (e.retryAfter && e.retryAfter > 0) {
        setRetryAfter(e.retryAfter);
        setError(`Too many attempts — try again in ${e.retryAfter}s`);
      } else {
        setError(e.message ?? "Google sign-in failed");
      }
    } finally {
      setLoading(false);
    }
  }, [loginWithGoogleCredential, rememberMe, clearClientSession, setLocation]);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError("");
    setAccountLocked(false);
    setRetryAfter(0);
    setLoading(true);
    try {
      clearClientSession();
      const tsToken = captchaTurnstile.token ?? "";
      const result = await login(email, password, tsToken, rememberMe);
      if (result?.requires2fa) {
        setChallenge(result);
      }
      captchaTurnstile.reset();
    } catch (err) {
      const e = err as Error & { accountLocked?: boolean; captchaRequired?: boolean; retryAfter?: number };
      if (e.accountLocked) setAccountLocked(true);
      if (e.captchaRequired) {
        setCaptchaRequired(true);
        captchaTurnstile.reset();
      }
      if (e.retryAfter && e.retryAfter > 0) {
        setRetryAfter(e.retryAfter);
        setError(`Too many attempts — try again in ${e.retryAfter}s`);
      } else {
        setError(e.message ?? "Invalid email or password");
      }
    } finally {
      setLoading(false);
    }
  }

  async function handle2FaSubmit(e: FormEvent) {
    e.preventDefault();
    if (!challenge) return;
    setError("");
    setLoading(true);
    try {
      await completeLogin(
        challenge.userId,
        challenge.challengeToken,
        twoFaCode.trim(),
        rememberMe,
        useRecovery,
      );
      setLocation(appHomePath());
    } catch (err) {
      setError((err as Error).message ?? "Verification failed");
    } finally {
      setLoading(false);
    }
  }

  function toggleRecovery() {
    setUseRecovery(v => !v);
    setTwoFaCode("");
    setError("");
    setResendMsg("");
  }

  async function handleResend() {
    if (!challenge || challenge.method !== "email") return;
    setResendLoading(true);
    setResendMsg("");
    try {
      await resendOtp(challenge.userId, challenge.challengeToken);
      setResendMsg(lg.enterEmailCodeHint);
    } catch (err) {
      setResendMsg((err as Error).message ?? "Failed to resend code");
    } finally {
      setResendLoading(false);
    }
  }

  function handleBackToLogin() {
    setChallenge(null);
    setTwoFaCode("");
    setError("");
    setResendMsg("");
    setUseRecovery(false);
  }

  // Button disabled logic
  const captchaBlocking = captchaRequired && (captchaTurnstile.isLoading || !captchaTurnstile.token);
  const isSubmitDisabled = loading || !email || !password || captchaBlocking || retryAfter > 0;

  // Button label
  function submitLabel() {
    if (loading) return <><Loader2 className="inline w-3.5 h-3.5 mr-1.5 animate-spin" />{lg.signingIn}</>;
    if (retryAfter > 0) return `Try again in ${retryAfter}s`;
    if (captchaRequired && captchaTurnstile.isLoading) return "Loading verification...";
    if (captchaRequired && !captchaTurnstile.token) return "Verification required";
    return lg.logIn;
  }

  const LangToggle = () => (
    <button
      type="button"
      onClick={() => setLanguage(isFr ? "en" : "fr")}
      className="absolute top-4 right-4 z-20 px-3 py-1 rounded-full border border-white/30 bg-black/30 text-white text-xs font-mono font-bold tracking-widest backdrop-blur-sm hover:bg-black/50 transition-all"
    >
      {isFr ? "EN" : "FR"}
    </button>
  );

  /* ── 2FA screen ──────────────────────────────────────────────── */
  if (challenge) {
    const isEmail = challenge.method === "email";
    const codeReady = useRecovery
      ? twoFaCode.replace(/[\s-]/g, "").length >= 8
      : twoFaCode.replace(/\s/g, "").length >= 6;

    return (
      <div
        className="min-h-screen flex items-center justify-center p-4 relative"
        style={{ backgroundImage: `url(${bgImage})`, backgroundSize: "cover", backgroundPosition: "center" }}
      >
        <div className="absolute inset-0 bg-black/55" />
        <LangToggle />
        <div className="relative z-10 w-full max-w-md bg-black/40 backdrop-blur-md rounded-2xl border border-white/15 shadow-2xl overflow-hidden">
          <div className="px-8 pt-10 pb-8">
            <div className="text-center mb-8">
              <div className="flex justify-center mb-3">
                <div className="w-12 h-12 rounded-full bg-white/10 border border-white/20 flex items-center justify-center">
                  {useRecovery
                    ? <KeyRound className="w-5 h-5 text-white" strokeWidth={1.5} />
                    : <ShieldCheck className="w-5 h-5 text-white" strokeWidth={1.5} />
                  }
                </div>
              </div>
              <h1 className="text-lg font-bold tracking-tight text-white font-mono">
                {useRecovery ? lg.recoveryCodeTitle : lg.twoFaTitle}
              </h1>
              {!useRecovery && (
                <div className="flex items-center justify-center gap-1.5 mt-2 text-white/60 text-xs font-mono">
                  {isEmail
                    ? <><Mail className="w-3.5 h-3.5" /> {lg.emailVerification}</>
                    : <><Smartphone className="w-3.5 h-3.5" /> {lg.authenticatorApp}</>
                  }
                </div>
              )}
              <p className="text-xs text-white/50 mt-2 leading-relaxed font-mono">
                {useRecovery
                  ? lg.enterRecoveryHint
                  : isEmail
                    ? lg.enterEmailCodeHint
                    : lg.enterAppCodeHint}
              </p>
            </div>

            <form onSubmit={handle2FaSubmit} className="space-y-4">
              <div>
                <label className={labelCls}>
                  {useRecovery ? lg.recoveryCodeLabel : lg.verificationCodeLabel}
                </label>
                {useRecovery ? (
                  <input key="recovery" type="text" value={twoFaCode} onChange={e => setTwoFaCode(e.target.value)}
                    className={`${inputCls} text-center tracking-widest`} placeholder="xxxxxxxx-xxxxxxxx"
                    autoFocus autoComplete="off" maxLength={20} />
                ) : (
                  <input key="totp" type="text" inputMode="numeric" pattern="[0-9 ]*" maxLength={7}
                    value={twoFaCode} onChange={e => setTwoFaCode(e.target.value)}
                    className={`${inputCls} text-center text-2xl tracking-[0.5em]`} placeholder="000000"
                    autoFocus autoComplete="one-time-code" />
                )}
              </div>

              {error && (
                <div className="text-red-300 text-xs border border-red-400/30 rounded-lg px-3 py-2.5 bg-red-500/15 font-mono">{error}</div>
              )}
              {resendMsg && (
                <div className="text-blue-300 text-xs border border-blue-400/30 rounded-lg px-3 py-2.5 bg-blue-500/15 font-mono">{resendMsg}</div>
              )}

              <button type="submit" disabled={loading || !codeReady}
                className="w-full bg-white text-gray-900 rounded-lg font-bold py-2.5 hover:bg-white/90 disabled:opacity-40 disabled:cursor-not-allowed transition-all text-sm font-mono tracking-wider">
                {loading ? lg.verifying : lg.verify}
              </button>
            </form>

            <div className="mt-5 space-y-2 text-center">
              {isEmail && !useRecovery && (
                <button onClick={handleResend} disabled={resendLoading}
                  className="flex items-center gap-1.5 mx-auto text-white/50 text-xs hover:text-white transition-colors disabled:opacity-40 font-mono">
                  <RefreshCw className={`w-3 h-3 ${resendLoading ? "animate-spin" : ""}`} />
                  {lg.resendCode}
                </button>
              )}
              <button onClick={toggleRecovery}
                className="flex items-center gap-1.5 mx-auto text-white/40 text-xs hover:text-white/70 transition-colors font-mono">
                <KeyRound className="w-3 h-3" />
                {useRecovery ? lg.useAuthEmail : lg.useRecovery}
              </button>
              <button onClick={handleBackToLogin}
                className="block w-full text-white/30 text-xs hover:text-white/60 transition-colors mt-1 font-mono">
                ← {lg.backToLogin}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  /* ── Login screen ────────────────────────────────────────────── */
  return (
    <>
      <div
        className="min-h-screen flex items-center justify-center px-4 relative"
        style={{ backgroundImage: `url(${bgImage})`, backgroundSize: "cover", backgroundPosition: "center" }}
      >
        <div className="absolute inset-0 bg-black/45" />
        <LangToggle />

        <div className="relative z-10 w-full max-w-md flex flex-col items-center gap-4">
          {/* Branding */}
          <div className="flex flex-col items-center gap-3 px-4">
            <img src={logoUrl} alt="Flight Deck" className="w-full max-w-[36rem] drop-shadow-2xl" />
            <p className="text-white/90 text-base sm:text-lg font-light tracking-wide drop-shadow-lg text-center leading-snug">
              {lg.tagline}
            </p>
          </div>

          {/* Card */}
          <div className="w-full bg-black/40 backdrop-blur-md rounded-2xl border border-white/15 shadow-2xl p-8">
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className={labelCls}>{lg.emailLabel}</label>
                <input
                  type="text"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  className={inputCls}
                  placeholder={lg.emailPlaceholder}
                  autoFocus
                  autoComplete="username"
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className={labelCls} style={{ marginBottom: 0 }}>{lg.passwordLabel}</label>
                  <button
                    type="button"
                    onClick={() => setShowForgot(true)}
                    className="text-xs text-white/60 hover:text-white transition-colors font-mono"
                  >
                    {lg.forgotPassword}
                  </button>
                </div>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    className={`${inputCls} pr-11 tracking-widest`}
                    placeholder="••••••••"
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(v => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white transition-colors"
                    tabIndex={-1}
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <label className="flex items-center gap-2.5 cursor-pointer group select-none">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={e => setRememberMe(e.target.checked)}
                  className="w-4 h-4 rounded border-white/30 text-primary focus:ring-white/20 bg-white/10"
                />
                <span className="text-xs text-white/60 group-hover:text-white transition-colors font-mono">
                  {lg.rememberMe}
                </span>
              </label>

              {/* ── Error display ── */}
              {error && (
                accountLocked ? (
                  <div className="text-xs border border-amber-400/30 rounded-lg px-4 py-3 bg-amber-500/15 space-y-1 font-mono">
                    <div className="text-amber-300 font-bold tracking-wider flex items-center gap-2">
                      ⚠ {lg.accountLocked}
                    </div>
                    <div className="text-amber-300/80 leading-relaxed">{error}</div>
                    <a onClick={handleSupport} className="block mt-1 text-amber-300 underline underline-offset-2 hover:text-amber-200 transition-colors">
                      → {lg.contactSupportUnlock}
                    </a>
                  </div>
                ) : (
                  <div className="text-red-300 text-xs border border-red-400/30 rounded-lg px-3 py-2.5 bg-red-500/15 font-mono">
                    {error}
                  </div>
                )
              )}

              {/* ── CAPTCHA section (appears only after 3 failures) ── */}
              {captchaRequired && (
                <div className="rounded-lg border border-yellow-500/30 bg-yellow-500/10 px-4 py-3 space-y-2">
                  <p className="text-xs text-yellow-200/90 font-mono font-semibold">
                    ⚠ Too many failed attempts — please complete the check below
                  </p>

                  {captchaTurnstile.scriptBlocked ? (
                    <p className="text-xs text-red-300/90 font-mono">
                      Verification failed to load — disable ad blocker and refresh
                    </p>
                  ) : captchaTurnstile.isLoading ? (
                    <p className="text-xs text-yellow-300/60 font-mono flex items-center gap-1.5">
                      <Loader2 className="w-3 h-3 animate-spin" />
                      Loading verification...
                    </p>
                  ) : captchaTurnstile.token ? (
                    <p className="text-xs text-green-400/90 font-mono flex items-center gap-1.5">
                      <CheckCircle2 className="w-3.5 h-3.5" />
                      Verified
                    </p>
                  ) : (
                    <div ref={captchaTurnstile.containerRef} className="flex justify-center" />
                  )}
                </div>
              )}

              <button
                type="submit"
                disabled={isSubmitDisabled}
                className="w-full bg-blue-600 text-white rounded-lg font-bold py-2.5 hover:bg-blue-500 disabled:opacity-40 disabled:cursor-not-allowed transition-all text-sm font-mono tracking-wider mt-1"
              >
                {submitLabel()}
              </button>
            </form>

            {googleClientId ? (
              <div className="mt-5 space-y-3">
                <div className="relative flex items-center gap-3">
                  <div className="h-px flex-1 bg-white/15" />
                  <span className="text-[10px] text-white/40 font-mono uppercase tracking-widest shrink-0">{lg.orDivider}</span>
                  <div className="h-px flex-1 bg-white/15" />
                </div>
                <GoogleGsiButton
                  clientId={googleClientId}
                  intent="login"
                  disabled={loading || retryAfter > 0}
                  onCredential={cred => { void handleGoogleLogin(cred); }}
                />
              </div>
            ) : null}

            <div className="mt-6 pt-5 border-t border-white/10 text-center space-y-3">
              <p className="text-sm text-white/60 font-mono">
                {lg.noAccount}{" "}
                <Link href="/signup" className="text-blue-400 hover:text-blue-300 transition-colors font-semibold underline underline-offset-2">
                  {lg.signUp}
                </Link>
              </p>
              <div className="flex justify-center gap-4">
                <Link href="/policies" className="text-[11px] text-white/30 hover:text-white/60 transition-colors font-mono">{isFr ? "Politiques" : "Policies"}</Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      {showForgot && <ForgotPasswordModal onClose={() => setShowForgot(false)} />}
    </>
  );
}
